import { Injectable } from '@angular/core';
import type { BackendAllErrorCodes } from '@freelancer/datastore';
import type { SecurePhone } from '@freelancer/datastore/collections';
import type { ResponseData } from '@freelancer/freelancer-http';
import {
  FreelancerHttp,
  transformResponseData,
} from '@freelancer/freelancer-http';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import type {
  PhoneVerifyRequestResultApi,
  PhoneVerifySubmitResultApi,
} from 'api-typings/users/users';
import { PhoneVerificationRequestTypeApi as RequestType } from 'api-typings/users/users';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import type {
  PhoneVerifyRequestResult,
  PhoneVerifySubmitResult,
} from './phone-verification.model';
import {
  transformVerificationConfirmation,
  transformVerificationRequest,
} from './phone-verification.transformers';

@UntilDestroy({ className: 'PhoneVerification' })
@Injectable({ providedIn: 'root' })
export class PhoneVerification {
  constructor(private freelancerHttp: FreelancerHttp) {}

  /**
   * Request to receive phone verification code.
   *
   * @param securePhone User's secure phone details
   * @param type Phone verification type: RequestType.SMS or RequestType.CALL
   * @param requestPhoneChange Trigger phone change request
   */
  request(
    securePhone: SecurePhone,
    type: RequestType = RequestType.SMS,
    requestPhoneChange: boolean = false,
  ): Promise<ResponseData<PhoneVerifyRequestResult, BackendAllErrorCodes>> {
    return firstValueFrom(
      this.freelancerHttp
        .post<PhoneVerifyRequestResultApi, BackendAllErrorCodes>(
          'users/0.1/phone_verifications',
          {
            phone_number: securePhone.phoneNumber,
            country_code: securePhone.countryCode,
            operation: type,
            requested_country: securePhone.country,
            request_phone_change: requestPhoneChange,
          },
        )
        .pipe(
          map(data =>
            transformResponseData(data, transformVerificationRequest),
          ),
          untilDestroyed(this),
        ),
    );
  }

  /**
   * Confirm phone given verification code.
   *
   * @param securePhone User's secure phone details
   * @param referenceId Verification request reference ID
   * @param code Verification code received
   * @param requestPhoneChange Trigger phone change request
   */
  confirm(
    securePhone: SecurePhone,
    referenceId: string,
    code: string,
    requestPhoneChange: boolean = false,
  ): Promise<ResponseData<PhoneVerifySubmitResult, BackendAllErrorCodes>> {
    return firstValueFrom(
      this.freelancerHttp
        .put<PhoneVerifySubmitResultApi, BackendAllErrorCodes>(
          'users/0.1/phone_verifications',
          {
            action: 'verify',
            verification_details: {
              reference_id: referenceId,
              phone_details: {
                phone_number: securePhone.phoneNumber,
                country_code: securePhone.countryCode,
                requested_country: securePhone.country,
              },
              request_phone_change: requestPhoneChange,
              verification_code: code,
            },
          },
        )
        .pipe(
          map(data =>
            transformResponseData(data, transformVerificationConfirmation),
          ),
          untilDestroyed(this),
        ),
    );
  }
}
